<template>
  <!-- <svg class="legend" :viewBox="viewBox"></svg> -->
  <ChartBase
    :height="`${height}px`"
    :style="{
      width: width ? width + 'px' : undefined,
      height: height + 'px',
      color: '#000000'
    }"
    :paddingOverride="{ top: padding, bottom: padding, left: 0, right: 0 }"
    :components="components"
  />
</template>
<script lang="ts" setup>
import { ChartComponent } from '@/components/AnalyticsDashboard/components/ChartComponent'
import ChartBase from '@/components/AnalyticsDashboard/components/ChartComponent/ChartBase.vue'
import { createBackgroundGradient } from '@/components/AnalyticsDashboard/components/ChartComponent/chart/backgroundGradient'
import { Limits } from '@/components/AnalyticsDashboard/components/ChartComponent/chart/limits'
import { FormatOptions, formatter } from '@/components/AnalyticsDashboard/formatter'
import { ColorScaleInterpolate } from '@/utils/colorScale'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

type Props = {
  width?: number
  height?: number
  colorScale: ColorScaleInterpolate
  formatOptions?: FormatOptions
  mean?: number
  hideMean?: boolean
  padding?: number
  labelStart?: string
  labelEnd?: string
}

const props = withDefaults(defineProps<Props>(), {
  width: undefined,
  height: 60,
  mean: undefined,
  formatOptions: undefined,
  padding: 20,
  hideMean: false,
  labelStart: undefined,
  labelEnd: undefined
})

const { locale } = useI18n()
const min = computed(() => props.colorScale.cutoffs[0].value ?? 0)
const max = computed(() => props.colorScale.cutoffs[props.colorScale.cutoffs.length - 1].value ?? 1)
const mean = computed(() => props.mean)
const colorScale = computed(() => props.colorScale)
const formatOptions = computed(() => props.formatOptions)
const limits = computed<Limits>(() => ({
  x: [min.value, max.value],
  y: [0, 1]
}))

const ticks = computed(() =>
  props.colorScale.cutoffs
    .map((c) => c.value ?? 0)
    .filter((v, i) => i === 0 || i === colorScale.value.cutoffs.length - 1)
)

const components = computed((): ChartComponent[] => {
  const meanValue = mean.value ?? 0
  const range = max.value - min.value
  const relativeMean = (meanValue - min.value) / range
  const anchor = relativeMean < 0.05 ? 'start' : relativeMean > 0.95 ? 'end' : 'middle'
  const x: number =
    anchor === 'start'
      ? min.value + 0.000000001
      : anchor === 'end'
      ? max.value - 0.000000001
      : meanValue
  const meanText: ChartComponent<'text'> = {
    type: 'text',
    text: formatter(mean.value, locale.value, formatOptions.value),
    font: { size: 12 },
    fill: '#000000',
    anchor,
    xLimits: [min.value, max.value],
    yLimits: [0, 1],
    x,
    y: -0.4
  }
  const meanComponents: ChartComponent[] = [
    meanText,
    {
      type: 'line',
      label: 'Mean',
      stroke: '#000000',
      strokeWidth: 1,
      xLimits: [min.value, max.value],
      yLimits: [0, 1],
      values: [
        [meanValue, -0.1],
        [meanValue, 1.1]
      ]
    }
  ]
  const axis: ChartComponent<'axis'> = {
    type: 'axis',
    position: 'bottom',
    font: { size: 12 },
    domain: [min.value, max.value],
    showTicks: false,
    showLine: false,
    tickLabels:
      props.labelStart || props.labelEnd
        ? [props.labelStart ?? '', props.labelEnd ?? '']
        : ticks.value.map((value) => formatter(value ?? 0, locale.value, formatOptions.value)),
    tickAligns: ['start', 'end'],
    ticks: ticks.value
  }
  return [
    ...createBackgroundGradient(colorScale.value, limits.value, 'horizontal', 1.0),
    ...(mean.value && !props.hideMean ? meanComponents : []),
    axis
  ]
})
</script>
<style lang="scss" scoped>
.legend {
  width: 100%;
}
</style>
